import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import useAutopilotChat from "../../hooks/useAutopilotChat";
import autopilotLogo from "../../assets/img/autopilot-logo.png";
import { AUTOPILOT_COLORS } from "./styles";
import "./ChatModal.css";

// Só http/https viram link. Sem isso um `[clique](javascript:...)` vindo do
// modelo viraria um vetor de execução dentro do chat.
const URL_SEGURA = /^https?:\/\//i;

// Pontuação final costuma ser da FRASE, não da URL: "veja em https://x.com/a."
const PONTUACAO_FINAL = /[.,;:!?)]+$/;

function elementoLink(texto, url, chave) {
  if (!URL_SEGURA.test(url)) return texto;

  return (
    <a
      key={chave}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: AUTOPILOT_COLORS.accent, textDecoration: "underline" }}
    >
      {texto}
    </a>
  );
}

// Inline do Markdown que o modelo realmente usa: **negrito**, [texto](url) e
// URL solta. Antes só o negrito era tratado, entao o link do rastreamento que a
// tool `get_delivery_status` devolve (PO1-2822) aparecia cru na tela, como
// "[link de rastreamento](https://...)", e o usuario nao tinha onde clicar.
function renderInline(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<]+)/g;
  let last = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));

    if (match[1] !== undefined) {
      parts.push(<strong key={match.index}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(elementoLink(match[2], match[3], match.index));
    } else {
      const bruta = match[4];
      const sufixo = (bruta.match(PONTUACAO_FINAL) || [""])[0];
      const url = sufixo ? bruta.slice(0, -sufixo.length) : bruta;
      parts.push(elementoLink(url, url, match.index));
      if (sufixo) parts.push(sufixo);
    }

    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// O LLM responde em Markdown (títulos ###, listas -/1., divisores ---, linhas
// em branco). O render antigo só entendia **negrito** e jogava fora as linhas
// em branco — "###" aparecia cru e o espaçamento entre seções dependia do
// "---" renderizado como texto. Este parser de blocos lê a estrutura que o
// modelo já manda e traduz em hierarquia visual de verdade.
function parseBlocks(content) {
  const blocks = [];
  for (const raw of String(content || "").split("\n")) {
    const t = raw.trim();
    if (!t) {
      blocks.push({ type: "gap" });
      continue;
    }
    if (/^([-*_])(\s*\1){2,}$/.test(t)) {
      // divisor horizontal: vira só respiro (o título seguinte já separa)
      blocks.push({ type: "gap" });
      continue;
    }
    const heading = t.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      blocks.push({ type: "heading", text: heading[1] });
      continue;
    }
    const bullet = t.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      blocks.push({ type: "li", text: bullet[1] });
      continue;
    }
    const numbered = t.match(/^(\d+)([.)])\s+(.*)$/);
    if (numbered) {
      blocks.push({ type: "oli", num: numbered[1], sep: numbered[2], text: numbered[3] });
      continue;
    }
    blocks.push({ type: "p", text: t });
  }
  return blocks;
}

// Espaçamento (spec final do Rodrigo, 12/06): o GATEWAY normaliza a resposta
// ("---" do modelo vira linha em branco; demais brancos removidos), então aqui
// linha em branco = fronteira de grupo, garantido. O render só traduz:
// branco -> UMA linha vazia literal; todo o resto colado (4px). Sem heurística
// de título/tipo — já tentamos e o estilo do modelo varia demais.
function renderMessageContent(content) {
  const rendered = [];
  let pendingGap = false;
  parseBlocks(content).forEach((block, i) => {
    if (block.type === "gap") {
      pendingGap = rendered.length > 0; // colapsa consecutivos; ignora no topo
      return;
    }
    if (pendingGap) {
      rendered.push(
        <p key={`gap-${i}`} style={{ margin: "4px 0 0" }} aria-hidden="true">
          {"\u00A0"}
        </p>
      );
      pendingGap = false;
    }
    const marginTop = rendered.length === 0 ? 0 : 4;
    if (block.type === "heading") {
      rendered.push(
        <p key={i} style={{ margin: `${marginTop}px 0 0`, fontWeight: 700 }}>
          {renderInline(block.text)}
        </p>
      );
    } else if (block.type === "li") {
      rendered.push(
        <p key={i} style={{ margin: `${marginTop}px 0 0`, paddingLeft: "14px" }}>
          {"• "}
          {renderInline(block.text)}
        </p>
      );
    } else if (block.type === "oli") {
      rendered.push(
        <p key={i} style={{ margin: `${marginTop}px 0 0`, paddingLeft: "14px" }}>
          {`${block.num}${block.sep} `}
          {renderInline(block.text)}
        </p>
      );
    } else {
      rendered.push(
        <p key={i} style={{ margin: `${marginTop}px 0 0` }}>
          {renderInline(block.text)}
        </p>
      );
    }
  });
  return rendered;
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: "10px" }}>
      {!isUser && (
        <img
          src={autopilotLogo}
          alt="Autopilot"
          style={{
            width: "26px", height: "26px", borderRadius: "50%",
            objectFit: "cover", marginRight: "7px", flexShrink: 0, marginTop: "2px",
          }}
        />
      )}
      <div style={{
        maxWidth: "85%", padding: "8px 12px",
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        background: isUser
          ? AUTOPILOT_COLORS.chatBubbleUserBg
          : AUTOPILOT_COLORS.chatBubbleAiBg,
        border: isUser ? "none" : `1px solid ${AUTOPILOT_COLORS.border}`,
        color: isUser ? AUTOPILOT_COLORS.chatBubbleUserText : AUTOPILOT_COLORS.chatBubbleAiText,
        fontSize: "0.82rem", lineHeight: "1.55",
      }}>
        {renderMessageContent(message.content)}
      </div>
    </div>
  );
}

// contextoInicial (opcional, PO1-3484): texto que acompanha só a PRIMEIRA mensagem que
// o usuário digita depois de abrir. Serve para a conversa pré-carregada (que não passou
// pelo chat) chegar ao Autopilot; na tela aparece só o que o usuário digitou. Depois
// disso a sessão do chat guarda o histórico.
const ChatModal = ({ isOpen, onClose, categoria, count, initialMessage, displayMessage, title, preloadedAnswer, preloadedUserMessage, sessionId, empresaId, contextoInicial }) => {
  const { messages, loading, error, sendMessage, resetWithSession, injectAssistantMessage, injectConversation } = useAutopilotChat(empresaId);
  const [input, setInput] = useState("");
  const [autoSent, setAutoSent] = useState(false);
  const messagesEndRef = useRef(null);
  const contextoPendente = useRef(null);

  useEffect(() => {
    contextoPendente.current = isOpen && contextoInicial ? contextoInicial : null;
  }, [isOpen, contextoInicial]);

  useEffect(() => {
    if (isOpen && !autoSent) {
      if (sessionId) {
        resetWithSession(sessionId);
      }
      if (preloadedAnswer && preloadedUserMessage) {
        injectConversation(preloadedUserMessage, preloadedAnswer);
        setAutoSent(true);
      } else if (preloadedAnswer) {
        injectAssistantMessage(preloadedAnswer);
        setAutoSent(true);
      } else {
        const msg = initialMessage
          || (categoria ? `No Monitor de Integrações, há ${count} CTe com a inconsistência "${categoria}" no backlog. Me explique o que causa esse erro e como posso resolver.` : null);
        if (msg) {
          sendMessage(msg, displayMessage ? { displayText: displayMessage } : undefined);
          setAutoSent(true);
        }
      }
    }
    if (!isOpen) {
      setAutoSent(false);
      resetWithSession();
    }
  }, [isOpen, categoria, initialMessage, preloadedAnswer, preloadedUserMessage, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  const textareaRef = useRef(null);

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    const contexto = contextoPendente.current;
    if (contexto) {
      contextoPendente.current = null;
      sendMessage(`${contexto}\n\nPedido do usuário: ${text}`, { displayText: text });
      return;
    }
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      fade={false}
      contentClassName="chat-modal-content"
      backdropClassName="chat-modal-backdrop"
      wrapClassName="chat-modal-wrap"
    >
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "var(--everlog-card-hero-bg)",
        borderBottom: "1px solid var(--everlog-card-border)",
        padding: "12px 16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <img
            src={autopilotLogo}
            alt="Autopilot"
            style={{ width: 28, height: 28, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
          />
          <div>
            <div style={{ color: AUTOPILOT_COLORS.accent, fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1 }}>
              Autopilot
            </div>
            <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: "0.88rem", lineHeight: 1.3 }}>
              {title || categoria}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: AUTOPILOT_COLORS.muted,
          fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px",
        }}>×</button>
      </div>

      {/* Messages */}
      <ModalBody style={{
        background: "var(--everlog-card-surface)",
        maxHeight: "52vh", overflow: "auto", padding: "16px 20px",
      }}>
        {messages.map((msg, i) => <MessageBubble key={i} message={msg} />)}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
            <img
              src={autopilotLogo}
              alt="Autopilot"
              style={{ width: "26px", height: "26px", borderRadius: "50%", objectFit: "cover" }}
            />
            <ClipLoader size={12} color={AUTOPILOT_COLORS.accent} />
          </div>
        )}
        {error && <p style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.78rem" }}>{error}</p>}
        <div ref={messagesEndRef} />
      </ModalBody>

      {/* Input */}
      <div style={{
        background: "var(--everlog-card-hero-bg)",
        borderTop: "1px solid var(--everlog-card-border)",
        padding: "10px 16px 14px",
      }}>
        <div style={{
          display: "flex", alignItems: "flex-end", gap: "8px",
          background: "var(--everlog-card-surface-elevated)",
          border: "1px solid var(--everlog-card-border)",
          borderRadius: "10px", padding: "8px 12px",
        }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Continue a conversa..."
            disabled={loading}
            rows={1}
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              color: "var(--everlog-card-text)", fontSize: "0.82rem", resize: "none",
              lineHeight: "1.5", overflowY: "auto", fontFamily: "inherit",
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{
              background: input.trim() && !loading
                ? AUTOPILOT_COLORS.chatBubbleUserBg
                : AUTOPILOT_COLORS.surface,
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "7px",
              width: "28px", height: "28px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: input.trim() && !loading ? "pointer" : "not-allowed",
              flexShrink: 0,
              color: input.trim() && !loading ? AUTOPILOT_COLORS.chatBubbleUserText : AUTOPILOT_COLORS.muted,
              fontSize: "13px",
            }}
          >{loading ? <ClipLoader size={11} color={AUTOPILOT_COLORS.accent} /> : "↑"}</button>
        </div>
      </div>
    </Modal>
  );
};

export default ChatModal;
